// Pure helpers for the shared policy screens (list, detail, editor review):
// the action a stored policy takes, how the list groups and orders policies,
// and the one-line words for Who, Action, Approval and Session. No React.
import { POLICY_GROUPS, policyActionLabel } from "./signals";

// policyFlow as the engine reads it: "mfa"/"deny" are folded, anything else
// it can't place reads as allow (AD, RADIUS, SSH, Apps). Database overrides
// this in its adapter (blank there means Require MFA).
export function flowOf(policyJson) {
  const j = policyJson || {};
  const raw = j.policyKind === "risk_based" ? j.aboveThresholdFlow || j.policyFlow : j.policyFlow;
  const v = String(raw || "").trim().toLowerCase();
  if (v === "mfa_required" || v === "mfa") return "mfa_required";
  if (v === "block" || v === "deny") return "block";
  return "allow";
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const orList = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
const andList = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

// The Who cell: "Everyone", "Domain Admins", "Domain Admins, +2 more".
export function whoCellText({ everyone, names = [] } = {}) {
  if (everyone) return "Everyone";
  const list = names.filter(Boolean);
  if (!list.length) return "Nobody";
  return list.length === 1 ? list[0] : `${list[0]}, +${list.length - 1} more`;
}

// The Who row of a review: "Everyone except breakglass@corp.com",
// "Users jdoe and asmith; groups Domain Admins".
// lists: [{ label: "Users", values: [] }]; except: string[].
export function whoReviewText({ everyone, lists = [], except = [] } = {}) {
  const ex = except.filter(Boolean);
  const tail = ex.length ? ` except ${andList(ex)}` : "";
  if (everyone) return `Everyone${tail}`;
  const parts = lists
    .filter((l) => (l.values || []).length)
    .map((l) => `${l.label.toLowerCase()} ${andList(l.values)}`);
  if (!parts.length) return "Nobody yet";
  const text = parts.join("; ");
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}${tail}`;
}

// "Require MFA", "Deny · Fallback".
export function actionReviewText(flow, fallback) {
  return [policyActionLabel(flow), fallback ? "Fallback" : ""].filter(Boolean).join(" · ");
}

// "From sec@corp.com or ops@corp.com; an approval lasts 30 minutes."
export function approvalReviewText(approval, { subject = "session" } = {}) {
  if (!approval?.required) return "Not required";
  const who = [...(approval.approvers || []), ...(approval.approverGroups || []).map((g) => `the ${g} group`)];
  const mins = Number(approval.validMinutes) || 0;
  const asks = [approval.requireJustification && "a reason", approval.requireTicket && "a ticket"].filter(Boolean);
  return [
    `From ${who.length ? orList(who) : "nobody yet"}`,
    asks.length ? `asks for ${andList(asks)}` : "",
    mins ? `an approval lasts ${plural(mins, "minute")}` : `an approval covers this ${subject} only`,
  ]
    .filter(Boolean)
    .join("; ")
    .concat(".");
}

// "Recorded; ends after 60 minutes or 15 minutes idle; re-verifies every 30 minutes."
export function sessionReviewText(session, { recording = true } = {}) {
  const s = session || {};
  const max = Number(s.maxDurationMinutes) || 0;
  const idle = Number(s.idleTimeoutMinutes) || 0;
  const reauth = Number(s.reauthIntervalMinutes) || 0;
  const ends = [max && `after ${plural(max, "minute")}`, idle && `after ${plural(idle, "minute")} idle`].filter(Boolean);
  const parts = [
    recording ? (s.record ? "Recorded" : "Not recorded") : "",
    ends.length ? `${recording ? "ends" : "Ends"} ${ends.join(" or ")}` : recording ? "no time limit" : "No time limit",
    reauth ? `re-verifies every ${plural(reauth, "minute")}` : "",
  ].filter(Boolean);
  return `${parts.join("; ")}.`;
}

// The detail page's subline: "Require MFA · Approval · Recorded".
export function detailSubline({ flow, fallback, approval, recorded } = {}) {
  return [policyActionLabel(flow), fallback && "Fallback", approval && "Approval", recorded && "Recorded"]
    .filter(Boolean)
    .join(" · ");
}

// Which list group a policy sits in.
export const groupKeyOf = ({ flow, fallback }) => (fallback ? "fallback" : flow);

// Default engine order inside a group, most specific first: policies naming
// people before Everyone, then more conditions first, then by name. An
// adapter can pass its own `specificity` (higher = more specific).
export function defaultSpecificity({ everyone, conditionCount = 0 }) {
  return (everyone ? 0 : 100) + conditionCount;
}

/**
 * Rows into the list's groups, in engine order (Deny, Require MFA, Allow,
 * Fallback), empty groups left out.
 *
 * items: [{ row, flow, fallback, everyone, conditionCount, name }]
 * options.mfaWinsOverAllow: Database (the Allow header says so).
 * options.specificity: (item) => number, higher first.
 * Returns [{ key, label, line, items }].
 */
export function groupPolicies(items, { mfaWinsOverAllow = false, specificity = defaultSpecificity } = {}) {
  return POLICY_GROUPS.map((g) => {
    const inGroup = items
      .filter((it) => groupKeyOf(it) === g.key)
      .sort((a, b) => specificity(b) - specificity(a) || String(a.name || "").localeCompare(String(b.name || "")));
    const line = g.key === "allow" && mfaWinsOverAllow ? "An MFA policy that also matches wins." : g.line;
    return { key: g.key, label: g.label, line, items: inGroup };
  }).filter((g) => g.items.length);
}

// "2 things need fixing".
export const fixCountText = (n) => (n === 1 ? "1 thing needs fixing" : `${n} things need fixing`);

/* ------------------------------------------------- editor summary (spec 3, 5) */

// "Alice", "Alice and Bob", "Alice, Bob and 3 more".
const shortAndList = (items, max = 3) => {
  if (items.length <= max) return andList(items);
  return `${items.slice(0, max - 1).join(", ")} and ${items.length - (max - 1)} more`;
};

/**
 * Who, for the sentence: "everyone", "jdoe and the Domain Admins group".
 * lists: [{ kind: "users" | "groups" | "ous" | other, values: [] }].
 */
export function whoSentenceText({ everyone, lists = [] } = {}) {
  if (everyone) return "everyone";
  const names = lists.flatMap((l) =>
    (l.values || []).filter(Boolean).map((v) => (l.kind === "groups" ? `the ${v} group` : l.kind === "ous" ? `the ${v} OU` : v)),
  );
  return names.length ? shortAndList(names) : "nobody yet";
}

const ACTION_VERB = { allow: "Allow", mfa_required: "Require MFA", block: "Deny" };

/**
 * The one-line summary under the form (spec 5), e.g.
 *   "Require MFA for sathyam@authnull.com on dbmfa_test as dbmfa_reader,
 *    outside Mon–Fri 09:00–18:00 (Asia/Kolkata), except breakglass@corp.com."
 * where: the product's phrase ("on dbmfa_test as dbmfa_reader"), or "".
 * clauses: conditionClauses(...). except: names. fallback adds " · fallback".
 */
export function policySentence({ flow, who = "nobody yet", where = "", clauses = [], except = [], fallback = false } = {}) {
  const head = `${ACTION_VERB[flow] || "Apply"} for ${who}${where ? ` ${where}` : ""}`;
  const ex = except.filter(Boolean);
  const parts = [head, ...clauses, ex.length ? `except ${shortAndList(ex)}` : ""].filter(Boolean);
  return `${parts.join(", ")}${fallback ? " · fallback" : ""}.`;
}

/**
 * The collapsed Advanced block's muted summary (spec 3):
 * "2 conditions · Except 1 · Fallback on", or "None".
 */
export function advancedSummaryText({ conditionCount = 0, exceptCount = 0, fallback = false, approval = false, session = false } = {}) {
  const parts = [
    conditionCount ? plural(conditionCount, "condition") : "",
    exceptCount ? `Except ${exceptCount}` : "",
    fallback ? "Fallback on" : "",
    approval ? "Approval on" : "",
    session ? "Session controls on" : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "None";
}
